import { COMPANY_AGREEMENT_STATUS, adminTypes } from 'helper/constant';
import { checkAdminAccess, isRouteRestricted } from 'helper/utility';

import { ACCOUNT_PENDING_STAGE, ACCOUNT_STATUS, AGREEMENT_STATUS } from './constants';

export const getAdminOptions = (member) => {
    const { verified, deleted_at } = member;

    let options = [
        { value: 'updateMember', label: 'Edit' },
        { value: 'changeRole', label: 'Change role' },
    ];

    if (verified !== undefined && deleted_at !== undefined) {
        if (verified && !deleted_at) {
            options.push({ value: 'suspend', label: 'Suspend' });
        } else if (!verified && !deleted_at) {
            options.push(
                { value: 'resendInvitation', label: 'Resend Invite' },
                { value: 'suspend', label: 'Suspend' }
            );
        } else {
            options = [{ value: 'reActivate', label: 'Re-activate' }];
        }
    } else {
        options = [];
    }

    return options;
};

/**
 * Which stage of activation a `pending` company is actually at.
 *
 * @param {object} company company object from the `companyInformation` selector
 * @returns {string} one of ACCOUNT_PENDING_STAGE
 */
const resolvePendingStage = (company) => {
    const { meta_data: metaData, agreementCreationStatus } = company || {};

    if (agreementCreationStatus === COMPANY_AGREEMENT_STATUS.CREATION_INPROGRESS) {
        return ACCOUNT_PENDING_STAGE.AGREEMENT_UNSIGNED;
    }

    if (!metaData) {
        return ACCOUNT_PENDING_STAGE.PAYMENT;
    }

    switch (metaData.agreement_status) {
        // Signed by the customer, waiting on our counter-signature — nothing for them to do.
        case AGREEMENT_STATUS.AWAITING_ADMIN:
            return ACCOUNT_PENDING_STAGE.AGREEMENT_IN_REVIEW;
        case AGREEMENT_STATUS.APPROVED:
            return ACCOUNT_PENDING_STAGE.PAYMENT;
        // initiated, awaiting_user, or not created yet.
        default:
            return ACCOUNT_PENDING_STAGE.AGREEMENT_UNSIGNED;
    }
};

// Resolve the status and sub-status shown in Settings → Account.
export const getAccountStatusPresentation = (company) => {
    const status = company?.status;
    const { label = '', subStatus = '', subStatusByStage } = ACCOUNT_STATUS[status] ?? {};

    return {
        status,
        label,
        subStatus: subStatusByStage?.[resolvePendingStage(company)] ?? subStatus,
    };
};

// Settings sections the logged-in admin can see. Used by the Settings page and the sidebar.
export const getVisibleSettingsSections = (adminType, user) => {
    const { COMPANY_ADMIN, COMPANY_OWNER } = adminTypes;
    const isOwner = checkAdminAccess(adminType, [COMPANY_OWNER]);
    const isOwnerOrAdmin = checkAdminAccess(adminType, [COMPANY_OWNER, COMPANY_ADMIN]);

    const sections = [{ key: 'account', label: 'Account' }];

    // Billing also stays hidden until the company agreement is signed.
    if (isOwner && !isRouteRestricted('Billing', user)) {
        sections.push({ key: 'billing', label: 'Billing' });
    }

    sections.push({ key: 'profile', label: 'Profile' });

    if (isOwner) {
        sections.push({ key: 'team', label: 'Team' });
    }

    if (isOwnerOrAdmin) {
        sections.push({ key: 'credentials', label: 'Credentials' });
    }

    return sections;
};

// The URL hash picks the section; an unknown or missing hash falls back to the first one.
export const getActiveSettingsSection = (sections, hash) => {
    const key = hash.replace('#', '').toLowerCase();

    return sections.find((section) => section.key === key) ?? sections[0];
};
